import React, { useEffect, useState } from 'react';
import { Input, TextArea, Dropdown, FileUpload } from '../forms';
import { ExtendedCreateProjectData, ProjectSpecification } from '@/types';
import { FileText, Plus, Edit, Trash2, AlertCircle, CheckCircle, Clock } from 'lucide-react';

interface SpecificationsStepProps {
  data: Partial<ExtendedCreateProjectData>;
  updateData: (data: Partial<ExtendedCreateProjectData>) => void;
  setValid: (isValid: boolean) => void;
}

const SpecificationsStep: React.FC<SpecificationsStepProps> = ({ data, updateData, setValid }) => {
  const [editingSpec, setEditingSpec] = useState<number | null>(null);
  const [newSpec, setNewSpec] = useState<ProjectSpecification>({
    title: '',
    category: '',
    description: '',
    priority: 'medium',
  });
  const [isAdding, setIsAdding] = useState(false);
  const [specFiles, setSpecFiles] = useState<Record<string, File[]>>({});

  const categories = [
    { value: 'general', label: 'General Requirements' },
    { value: 'site-work', label: 'Site Work' },
    { value: 'concrete', label: 'Concrete' },
    { value: 'masonry', label: 'Masonry' },
    { value: 'metals', label: 'Metals' },
    { value: 'wood-plastics', label: 'Wood, Plastics & Composites' },
    { value: 'thermal-moisture', label: 'Thermal & Moisture Protection' },
    { value: 'openings', label: 'Openings (Doors & Windows)' },
    { value: 'finishes', label: 'Finishes' },
    { value: 'specialties', label: 'Specialties' },
    { value: 'equipment', label: 'Equipment' },
    { value: 'furnishings', label: 'Furnishings' },
    { value: 'fire-suppression', label: 'Fire Suppression' },
    { value: 'plumbing', label: 'Plumbing' },
    { value: 'hvac', label: 'HVAC' },
    { value: 'electrical', label: 'Electrical' },
    { value: 'communications', label: 'Communications' },
    { value: 'other', label: 'Other' },
  ];

  const priorities = [
    { value: 'low', label: 'Low Priority' },
    { value: 'medium', label: 'Medium Priority' },
    { value: 'high', label: 'High Priority' },
  ];

  const priorityIcons = {
    low: <Clock className="h-4 w-4 text-green-500" />,
    medium: <AlertCircle className="h-4 w-4 text-yellow-500" />,
    high: <AlertCircle className="h-4 w-4 text-red-500" />,
  };

  const priorityColors = {
    low: 'bg-green-50 text-green-800 border-green-200',
    medium: 'bg-yellow-50 text-yellow-800 border-yellow-200',
    high: 'bg-red-50 text-red-800 border-red-200',
  };

  const validateForm = () => {
    // Always valid - specifications are optional
    setValid(true);
    return true;
  };

  useEffect(() => {
    validateForm();
  }, [data.specifications]);

  const addSpecification = () => {
    if (
      !newSpec.title ||
      typeof newSpec.title !== 'string' ||
      !newSpec.title.trim() ||
      !newSpec.category ||
      !newSpec.description ||
      typeof newSpec.description !== 'string' ||
      !newSpec.description.trim()
    ) {
      return;
    }

    const specification: ProjectSpecification = {
      ...newSpec,
      id: `spec-${Date.now()}`,
    };

    const updatedSpecs = [...(data.specifications || []), specification];
    updateData({ specifications: updatedSpecs });

    // Reset form
    setNewSpec({
      title: '',
      category: '',
      description: '',
      priority: 'medium',
    });
    setIsAdding(false);
  };

  const updateSpecification = (index: number, updates: Partial<ProjectSpecification>) => {
    const updatedSpecs = [...(data.specifications || [])];
    updatedSpecs[index] = { ...updatedSpecs[index], ...updates };
    updateData({ specifications: updatedSpecs });
  };

  const removeSpecification = (index: number) => {
    const updatedSpecs = [...(data.specifications || [])];
    const removedSpec = updatedSpecs.splice(index, 1)[0];

    // Remove associated files
    if (removedSpec.id && specFiles[removedSpec.id]) {
      const newSpecFiles = { ...specFiles };
      delete newSpecFiles[removedSpec.id];
      setSpecFiles(newSpecFiles);
    }

    updateData({ specifications: updatedSpecs });
  };

  const handleFileChange = (specId: string, files: File[]) => {
    setSpecFiles(prev => ({
      ...prev,
      [specId]: files,
    }));

    // Update the specification with file reference
    const specIndex = (data.specifications || []).findIndex(spec => spec.id === specId);
    if (specIndex !== -1 && files.length > 0) {
      updateSpecification(specIndex, { file: files[0] });
    }
  };

  const specsByCategory = categories.map(category => ({
    ...category,
    specifications: (data.specifications || []).filter(spec => spec.category === category.value),
  }));

  const specsByPriority = priorities.map(priority => ({
    ...priority,
    specifications: (data.specifications || []).filter(spec => spec.priority === priority.value),
  }));

  return (
    <div className="space-y-6 font-['Inter',sans-serif]">
      <style>
        {`
          @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');
        `}
      </style>

      {/* Overview */}
      <div className="bg-brand-50 border border-brand-200 rounded-lg p-4">
        <h4 className="text-base font-bold text-brand-900 mb-2 leading-[1.4em]">
          Project Specifications
        </h4>
        <p className="text-sm text-brand-700 font-normal leading-[1.75em]">
          Define technical specifications, requirements, and standards for your project. Organize by
          category and set priority levels for implementation.
        </p>
      </div>

      {/* Add New Specification */}
      <div className="border border-gray-200 rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h4 className="text-lg font-bold text-[#1F1F1F] leading-[1.4em]">Add Specification</h4>
          {!isAdding && (
            <button
              onClick={() => setIsAdding(true)}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-bold rounded-lg text-white bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-hover)] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[var(--brand-primary)] transition-colors uppercase tracking-wide leading-[1.2102272851126534em]"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Specification
            </button>
          )}
        </div>

        {isAdding && (
          <div className="space-y-4 p-4 bg-gray-50 rounded-lg">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Specification Title"
                value={newSpec.title}
                onChange={e => setNewSpec(prev => ({ ...prev, title: e.target.value }))}
                placeholder="Enter specification title"
                required
              />
              <Dropdown
                label="Category"
                options={categories}
                value={newSpec.category}
                onChange={value => setNewSpec(prev => ({ ...prev, category: value }))}
                placeholder="Select category"
                required
              />
            </div>

            <TextArea
              label="Description"
              value={newSpec.description}
              onChange={value => setNewSpec(prev => ({ ...prev, description: value }))}
              placeholder="Describe the specification requirements, standards, and details..."
              rows={4}
              required
            />

            <Dropdown
              label="Priority"
              options={priorities}
              value={newSpec.priority}
              onChange={value =>
                setNewSpec(prev => ({ ...prev, priority: value as 'low' | 'medium' | 'high' }))
              }
            />

            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setIsAdding(false);
                  setNewSpec({
                    title: '',
                    category: '',
                    description: '',
                    priority: 'medium',
                  });
                }}
                className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-[#1F1F1F] bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[var(--brand-primary)] transition-colors leading-[1.75em]"
              >
                Cancel
              </button>
              <button
                onClick={addSpecification}
                disabled={!newSpec.title || !newSpec.category || !newSpec.description}
                className="px-4 py-2 border border-transparent rounded-lg text-sm font-bold text-white bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-hover)] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[var(--brand-primary)] disabled:opacity-50 disabled:cursor-not-allowed transition-colors uppercase tracking-wide leading-[1.2102272851126534em]"
              >
                Add Specification
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Specifications List */}
      {data.specifications && data.specifications.length > 0 && (
        <div className="border border-gray-200 rounded-lg p-4">
          <h4 className="text-lg font-medium text-gray-900 mb-4">Project Specifications</h4>

          <div className="space-y-4">
            {(data.specifications || []).map((spec, index) => (
              <div
                key={spec.id || index}
                className={`border rounded-lg p-4 ${priorityColors[spec.priority]}`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    {editingSpec === index ? (
                      <div className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <Input
                            label="Title"
                            value={spec.title}
                            onChange={e => updateSpecification(index, { title: e.target.value })}
                          />
                          <Dropdown
                            label="Category"
                            options={categories}
                            value={spec.category}
                            onChange={value => updateSpecification(index, { category: value })}
                          />
                        </div>

                        <TextArea
                          label="Description"
                          value={spec.description}
                          onChange={value => updateSpecification(index, { description: value })}
                          rows={3}
                        />

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <Dropdown
                            label="Priority"
                            options={priorities}
                            value={spec.priority}
                            onChange={value =>
                              updateSpecification(index, {
                                priority: value as 'low' | 'medium' | 'high',
                              })
                            }
                          />
                          <div>
                            <FileUpload
                              label="Specification Document"
                              accept=".pdf,.doc,.docx,.txt"
                              files={spec.id ? specFiles[spec.id] || [] : []}
                              onChange={files => spec.id && handleFileChange(spec.id, files)}
                              maxFiles={1}
                              maxSize={10}
                              hint="Optional: Upload detailed specification document"
                            />
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div>
                        <div className="flex items-center space-x-3 mb-2">
                          {priorityIcons[spec.priority]}
                          <h5 className="font-semibold text-gray-900">{spec.title}</h5>
                          <span className="text-sm px-2 py-1 bg-brand-100 text-brand-800 rounded">
                            {categories.find(cat => cat.value === spec.category)?.label}
                          </span>
                        </div>
                        <p className="text-gray-700 mb-2">{spec.description}</p>
                        {spec.file && (
                          <div className="flex items-center space-x-2 text-sm text-gray-600">
                            <FileText className="h-4 w-4" />
                            <span>
                              {spec.file instanceof File
                                ? spec.file.name
                                : 'Specification document attached'}
                            </span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="flex space-x-2 ml-4">
                    {editingSpec === index ? (
                      <>
                        <button
                          onClick={() => setEditingSpec(null)}
                          className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
                        >
                          <CheckCircle className="h-4 w-4" />
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          onClick={() => setEditingSpec(index)}
                          className="p-1 text-gray-400 hover:text-brand-primary transition-colors"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => removeSpecification(index)}
                          className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Summary */}
      {data.specifications && data.specifications.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* By Category */}
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <h4 className="text-sm font-medium text-green-800 mb-3">By Category</h4>
            <div className="space-y-2">
              {specsByCategory
                .filter(category => category.specifications.length > 0)
                .map(category => (
                  <div key={category.value} className="flex justify-between items-center">
                    <span className="text-sm text-green-700">{category.label}</span>
                    <span className="text-sm bg-green-100 text-green-800 px-2 py-1 rounded">
                      {category.specifications.length}
                    </span>
                  </div>
                ))}
            </div>
          </div>

          {/* By Priority */}
          <div className="bg-brand-50 border border-brand-200 rounded-lg p-4">
            <h4 className="text-sm font-medium text-brand-800 mb-3">By Priority</h4>
            <div className="space-y-2">
              {specsByPriority
                .filter(priority => priority.specifications.length > 0)
                .map(priority => (
                  <div key={priority.value} className="flex justify-between items-center">
                    <div className="flex items-center space-x-2">
                      {priorityIcons[priority.value as keyof typeof priorityIcons]}
                      <span className="text-sm text-brand-700">{priority.label}</span>
                    </div>
                    <span className="text-sm bg-brand-100 text-brand-800 px-2 py-1 rounded">
                      {priority.specifications.length}
                    </span>
                  </div>
                ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SpecificationsStep;
